<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import { PencilSquareIcon, PaperAirplaneIcon, StarIcon } from '@heroicons/vue/24/outline'
import { useOrderStore } from '@/stores/useOrderStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useToast } from 'vue-toast-notification'

const props = defineProps<{ orderUuid: string }>()

const orderStore = useOrderStore()
const boostCoreStore = useBoostCorePermissionStore()
const $toast = useToast()

const comments = computed(() => orderStore.currentOrder?.operatorComments ?? [])
const currentUserUuid = computed(() => boostCoreStore.user?.uuid)
const canWrite = computed(
  () => boostCoreStore.isProjectWrite || boostCoreStore.can('ADV_WRITE') || boostCoreStore.isSuperAdmin,
)

const listRef = ref<HTMLElement | null>(null)
const input = ref('')
const isSubmitting = ref(false)
const isFavoriting = ref(false)
const editingUuid = ref<string | null>(null)
const editingText = ref('')

watch(comments, () => {
  nextTick(() => {
    if (listRef.value) listRef.value.scrollTop = listRef.value.scrollHeight
  })
}, { immediate: true })

async function onAdd() {
  if (!props.orderUuid || !input.value.trim()) return
  isSubmitting.value = true
  try {
    const created = await orderStore.addOperatorComment(props.orderUuid, input.value.trim())
    if (orderStore.currentOrder) {
      orderStore.currentOrder.operatorComments = [...(orderStore.currentOrder.operatorComments ?? []), created]
    }
    input.value = ''
    $toast.success('Commentaire ajouté')
  } catch {
    $toast.error("Erreur lors de l'ajout du commentaire")
  } finally {
    isSubmitting.value = false
  }
}

function startEdit(c: any) {
  editingUuid.value = c.uuid
  editingText.value = c.comment
}

function cancelEdit() {
  editingUuid.value = null
  editingText.value = ''
}

async function onSave() {
  if (!props.orderUuid || !editingUuid.value || !editingText.value.trim()) return
  isSubmitting.value = true
  try {
    const updated = await orderStore.updateOperatorComment(props.orderUuid, editingUuid.value, editingText.value.trim())
    if (orderStore.currentOrder) {
      orderStore.currentOrder.operatorComments = (orderStore.currentOrder.operatorComments ?? []).map((c: any) =>
        c.uuid === updated.uuid ? updated : c,
      )
    }
    cancelEdit()
    $toast.success('Commentaire modifié')
  } catch {
    $toast.error('Erreur lors de la modification du commentaire')
  } finally {
    isSubmitting.value = false
  }
}

async function onFavorite(commentUuid: string) {
  if (!props.orderUuid || isFavoriting.value) return
  isFavoriting.value = true
  try {
    const updated = await orderStore.favoriteOperatorComment(props.orderUuid, commentUuid)
    if (orderStore.currentOrder) {
      orderStore.currentOrder.operatorComments = (orderStore.currentOrder.operatorComments ?? []).map((c: any) =>
        c.uuid === updated.uuid ? updated : { ...c, favorite: false },
      )
    }
  } catch {
    $toast.error('Erreur lors de la mise en favori')
  } finally {
    isFavoriting.value = false
  }
}

async function onDelete(commentUuid: string) {
  if (!props.orderUuid) return
  isSubmitting.value = true
  try {
    const updated = await orderStore.deleteOperatorComment(props.orderUuid, commentUuid)
    if (orderStore.currentOrder) {
      orderStore.currentOrder.operatorComments = (orderStore.currentOrder.operatorComments ?? []).map((c: any) =>
        c.uuid === updated.uuid ? updated : c,
      )
    }
    $toast.success('Commentaire supprimé')
  } catch {
    $toast.error('Erreur lors de la suppression du commentaire')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- En-tête -->
    <div class="flex items-center px-6 pt-5 pb-3 border-b border-[#27E8E8]/30 flex-shrink-0">
      <h2 class="card-title text-lg gap-2">
        <PencilSquareIcon class="w-5 h-5" style="color: #27E8E8" />
        Commentaires Opérateur
        <span v-if="comments.length" class="badge badge-sm" style="background-color: #27E8E8; color: #000;">{{ comments.length }}</span>
      </h2>
    </div>

    <!-- Bulles SMS -->
    <div ref="listRef" class="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-2">
      <p v-if="!comments.length" class="text-sm text-center text-base-content/40 italic py-10">
        Aucun message pour l'instant.
      </p>
      <template v-for="c in comments" :key="c.uuid">

        <!-- Mon message (droite) -->
        <div v-if="c.author?.uuid === currentUserUuid" class="flex flex-col items-end gap-0.5">
          <div
            class="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-br-sm text-sm whitespace-pre-wrap break-words"
            :class="c.deleted ? 'bg-base-200/60 italic text-base-content/40' : 'text-base-content'"
            :style="!c.deleted ? 'background-color: rgba(39,232,232,0.18); border: 1px solid rgba(39,232,232,0.45);' : ''"
          >
            <template v-if="editingUuid === c.uuid">
              <textarea
                v-model="editingText"
                class="textarea textarea-bordered w-full text-sm h-24 min-w-[240px]"
                :disabled="isSubmitting"
              />
              <div class="flex gap-2 mt-2 justify-end">
                <button type="button" class="btn btn-ghost btn-xs" @click="cancelEdit">Annuler</button>
                <button
                  type="button"
                  class="btn btn-xs"
                  style="background-color: #27E8E8; color: #000;"
                  :disabled="isSubmitting || !editingText.trim()"
                  @click="onSave"
                >Enregistrer</button>
              </div>
            </template>
            <template v-else>{{ c.comment }}</template>
          </div>
          <div class="flex items-center gap-2 pr-1">
            <span class="text-[10px] text-base-content/40">
              {{ c.createdAt ? new Date(c.createdAt).toLocaleString('fr-FR') : '' }}
              <span v-if="c.editedAt" class="italic">(modifié)</span>
            </span>
            <button
              type="button"
              class="btn btn-ghost btn-xs p-0.5 h-auto min-h-0"
              :class="c.favorite ? 'text-amber-400' : 'text-base-content/20 hover:text-amber-400'"
              :title="c.favorite ? 'Retirer des favoris' : 'Mettre en favori'"
              :disabled="isFavoriting"
              @click="onFavorite(c.uuid)"
            >
              <StarIcon class="w-3.5 h-3.5" :class="{ 'fill-current': c.favorite }" />
            </button>
            <template v-if="!c.deleted && canWrite && editingUuid !== c.uuid">
              <button
                type="button"
                class="btn btn-ghost btn-xs p-0.5 h-auto min-h-0 opacity-60 hover:opacity-100"
                title="Modifier"
                @click="startEdit(c)"
              >
                <PencilSquareIcon class="w-3 h-3" />
              </button>
              <button
                type="button"
                class="btn btn-ghost btn-xs p-0.5 h-auto min-h-0 opacity-60 hover:opacity-100 text-error"
                title="Supprimer"
                :disabled="isSubmitting"
                @click="onDelete(c.uuid)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </template>
          </div>
        </div>

        <!-- Message d'un autre (gauche) -->
        <div v-else class="flex flex-col items-start gap-0.5">
          <span class="text-xs font-medium text-base-content/60 pl-1">
            {{ c.author?.firstName }} {{ c.author?.lastName || c.author?.email }}
          </span>
          <div
            class="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-bl-sm text-sm whitespace-pre-wrap break-words"
            :class="c.deleted ? 'bg-base-200/60 italic text-base-content/40' : 'bg-base-200 text-base-content'"
          >
            {{ c.comment }}
          </div>
          <div class="flex items-center gap-2 pl-1">
            <span class="text-[10px] text-base-content/40">
              {{ c.createdAt ? new Date(c.createdAt).toLocaleString('fr-FR') : '' }}
            </span>
            <button
              type="button"
              class="btn btn-ghost btn-xs p-0.5 h-auto min-h-0"
              :class="c.favorite ? 'text-amber-400' : 'text-base-content/20 hover:text-amber-400'"
              :title="c.favorite ? 'Retirer des favoris' : 'Mettre en favori'"
              :disabled="isFavoriting"
              @click="onFavorite(c.uuid)"
            >
              <StarIcon class="w-3.5 h-3.5" :class="{ 'fill-current': c.favorite }" />
            </button>
          </div>
        </div>

      </template>
    </div>

    <!-- Zone de saisie -->
    <div v-if="canWrite" class="flex items-end gap-2 px-4 pb-4 pt-2 flex-shrink-0 border-t border-[#27E8E8]/20">
      <textarea
        v-model="input"
        rows="1"
        class="textarea textarea-bordered flex-1 text-sm resize-none leading-snug"
        style="min-height: 40px; max-height: 120px; overflow-y: auto;"
        placeholder="Message..."
        :disabled="isSubmitting"
        @keydown.enter.exact.prevent="onAdd"
      />
      <button
        type="button"
        class="btn btn-sm btn-square flex-shrink-0"
        style="background-color: #27E8E8; border-color: #27E8E8; color: #000;"
        :disabled="isSubmitting || !input.trim()"
        @click="onAdd"
      >
        <PaperAirplaneIcon class="w-4 h-4" />
      </button>
    </div>
  </div>
</template>
